import type { ActionTree, MutationTree, GetterTree } from 'vuex'
import RootState from '@web/store/types'
import type { Category, CategoriesState } from '@club/types/Category'
import CategoryAPI from '@club/api/modules/category'
import type { Community } from '@club/types/Community'

const state: CategoriesState = {
  categories: {
    unknown: {}
  }
}

const categoryAPI = new CategoryAPI()

const sorter = (a: Category, b: Category): number => b.sort - a.sort
const getters: GetterTree<CategoriesState, RootState> = {
  categories: ({ categories }): Category[] => {
    let _categories: Record<string, Category> = {}
    Object.values(categories).forEach(categoriesByClub => {
      _categories = {
        ..._categories,
        ...categoriesByClub
      }
    })
    return Object.values<Category>(_categories).sort(sorter)
  },
  categoriesByClubId: ({ categories }, getters, rootState, rootGetters): Category[] | undefined => {
    const selectedCommunity: Community | null = rootGetters['clubCommunity/selectedCommunity']
    let clubId = 'unknown'
    if (selectedCommunity) {
      clubId = selectedCommunity.id.toString()
    }

    const _categories: Category[] = []
    for (const key in categories[clubId]) {
      _categories.push(categories[clubId][key])
    }

    return _categories.sort(sorter)
  }
}

const mutations: MutationTree<CategoriesState> = {
  addCategory (state: CategoriesState, payloads: { category: Category, clubId: string | number }) {
    state.categories[payloads.clubId][payloads.category.id] = payloads.category
  },
  addCategories (state: CategoriesState, payloads: { categories: Category[], clubId: string }) {
    payloads.categories.forEach((category) => {
      if (!state.categories[payloads.clubId]) {
        state.categories[payloads.clubId] = {}
      }
      state.categories[payloads.clubId][category.id] = category
    })
  },
  logout (state: CategoriesState) {
    state.categories = {}
  }
}

const actions: ActionTree<CategoriesState, RootState> = {
  async fetchCategories ({ commit, rootGetters }) {
    const selectedCommunity: Community | null = rootGetters['clubCommunity/selectedCommunity']
    if (!selectedCommunity) {
      throw new Error('Need to choose a community')
    }

    const options: {
      communityId?: number;
      communityExternalIds?: string[];
    } = {}

    if (selectedCommunity) {
      options.communityExternalIds = [selectedCommunity.uniq]
    }

    const categories = await categoryAPI.fetchAll(options)
    commit('addCategories', {
      categories: categories.items,
      clubId: selectedCommunity?.id.toString() || 'unknown'
    })
    return categories
  },
  fetchCachedCategories ({ getters, dispatch }) {
    if (getters.categoriesByClubId.length) {
      return getters.categoriesByClubId
    }

    return dispatch('fetchCategories')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
